/* תוכנית יציאה מהמינוס, "אפשר להוציא", חיסכון לתקופה וקצב הוצאה.

   בחשבון שנכנסת אליו משכורת יש שני מספרים, ושניהם אמיתיים:
   ‣ סוף התקופה (אחרי המשכורת וחיוב הכרטיס): המינוס שבאמת נשאר. שלב 1 = להביא אותו לאפס.
   ‣ השפל לפני המשכורת: גם כשסוף התקופה חיובי, באמצע החודש היתרה יורדת. שלב 2 = שגם השפל יהיה מעל אפס.
   שלב 3 = כרית ביטחון (השפל מעל הכרית). שלב 4 = חיסכון. */
import { S, memo } from './state.js';
import { todayISO, iso10, dDays, absAmt, median, fMoney } from '../lib/format.js';
import { curKey, keyAdd, periodStart, periodEnd } from './periods.js';
import { isExcluded, filterActive } from './tx.js';
import { balEst, periodOpening } from './balance.js';
import { project, projToEnd, daysToEnd, lowPoint, monthlyParts, monthlyNet } from './forecast.js';
import { fixedMatcher, recOpen } from './recurring.js';
import { savGoalStats, isSavingTx, isSavingRec, SAV_RX, savUnassigned } from './savings.js';

/** כמה מ-3 התקופות שלפני הנוכחית יש בהן הוצאות. הקצב וההערכות צריכים לפחות 2 */
export function histPeriods() {
  const k = curKey();
  let n = 0;
  for (let q = 1; q <= 3; q++) {
    const qk = keyAdd(k, -q);
    if (S.tx.some((t) => t.month === qk && t.type === 'expense')) n++;
  }
  return n;
}

export function exitStageGroup(st) {
  return st <= 2 ? 'exit' : String(st);
}

export function exitPlan() {
  if (!S.set.balDate) return null;
  return memo('exitPlan', exitPlanRaw);
}
function exitPlanRaw() {
  const t0 = new Date(todayISO() + 'T12:00:00');
  const pe = periodEnd(curKey());
  const p = projToEnd();
  const today = balEst().amount;
  const end = p.length ? p[p.length - 1].balance : today;
  let low = { balance: today, date: t0 };
  p.forEach((x) => { if (x.balance < low.balance) low = x; });
  const lowBal = low.balance, lowDate = low.date;
  const surplus = Math.round(monthlyNet());
  const debt = Math.max(0, -end);
  const swing = Math.max(0, Math.round(end - lowBal));
  const bufTarget = S.set.bufTarget;
  const buf = bufTarget > 0 ? bufTarget : 0;
  const out = {
    end: Math.round(end), endDate: pe, low: Math.round(lowBal), lowDate, debt: Math.round(debt), swing, surplus, buf, bufUnset: !bufTarget,
  };
  // bufTarget=0: עוד לא נבחרה כרית. 1-: בחרת לוותר עליה
  out.stage = debt > 0 ? 1 : lowBal < 0 ? 2 : !bufTarget || (buf > 0 && lowBal < buf) ? 3 : 4;
  out.group = exitStageGroup(out.stage);
  out.done = out.stage === 4;
  if (out.done) return out;
  const need = out.stage === 1 ? debt : out.stage === 2 ? -lowBal : buf ? buf - lowBal : 0;
  out.need = Math.round(Math.max(0, need));
  // תאריך יעד שייך לשלב שבו הוא נקבע. כשעוברים שלב, בוחרים תאריך חדש
  const { exitTarget, exitStage } = S.set;
  out.target = exitTarget && (exitStage || 'exit') === out.group ? exitTarget : '';
  if (out.target && need > 0) {
    const months = Math.max(0.5, (new Date(out.target + 'T12:00:00') - t0) / 86400000 / 30.44);
    out.months = months;
    out.required = Math.round(need / months);
    out.gap = Math.max(0, out.required - surplus);
  }
  if (surplus > 0 && need > 0) {
    const m = need / surplus, d = new Date(t0);
    d.setDate(d.getDate() + Math.round(m * 30.44));
    out.eta = d;
    out.etaMonths = m;
  }
  return out;
}

/** החיסכון בתקופה הנוכחית: מה כבר הועבר, מה מתוכנן עד סוף התקופה, ומה היעד החודשי */
export function savPeriod() {
  return memo('savPeriod', savPeriodRaw);
}
function savPeriodRaw() {
  const k = curKey(), list = [];
  let done = 0;
  S.tx.forEach((t) => {
    if (t.month !== k || !isSavingTx(t)) return;
    const a = absAmt(t);
    done += t.type === 'expense' ? a : -a;
    list.push(t);
  });
  let planned = 0;
  const plist = [];
  if (S.set.balDate) {
    projToEnd().forEach((x) => x.events.forEach((v) => {
      if (v.type === 'income' || v.card) return;
      const r = v.recId != null ? S.rec.find((q) => q.id === v.recId) : null;
      if ((r && isSavingRec(r)) || (!r && SAV_RX.test(v.name || ''))) {
        planned += v.amount;
        plist.push({ date: x.date, name: v.name, amount: v.amount });
      }
    }));
  }
  const goals = S.savings.map(savGoalStats);
  const M = goals.reduce((s, x) => s + x.monthly, 0);
  return { done, list, planned, plist, M, goals, left: Math.max(0, M - done - planned) };
}

/** כרית ביטחון מומלצת: חודש של יציאות מהעו"ש (חיובים קבועים, חיוב כרטיס טיפוסי והוצאות שוטפות), מעוגל ל-500 */
export function bufSuggest() {
  const M = monthlyParts();
  return Math.max(1000, Math.round((M.exp + M.card + M.vexp) / 500) * 500);
}

/* אפשר להוציא היום. מבוסס על התזרים עצמו, ולכן עובד גם בלי היסטוריה:
   ‣ היתרה הצפויה בסוף התקופה, אם לא יוצא שום דבר מעבר למה שכבר ידוע
   ‣ פחות היעד לסוף התקופה: יתרת הפתיחה (לא לסיים גרוע ממה שהתחלת), ועוד החיסכון שהתוכנית דורשת.
     במינוס בלי תאריך יעד: ועוד העודף החודשי (עד גובה המינוס), כדי שכל תקופה תקטין את המינוס ולא רק תשמור עליו.
   מה שנשאר מתחלק בימים עד סוף התקופה, כולל היום. */
export function safeToSpend() {
  if (!S.set.balDate) return null;
  return memo('safeToSpend', safeRaw);
}
function safeRaw() {
  const t0 = new Date(todayISO() + 'T12:00:00');
  const pe = periodEnd(curKey());
  const ahead = daysToEnd();
  const pr = projToEnd();
  const end = pr.length ? pr[pr.length - 1].balance : balEst().amount;
  const open = periodOpening();
  const P = exitPlan();
  let save = 0, SPv = null, saveAuto = false;
  if (P && P.stage < 4 && P.target && P.required) save = P.required;
  else if (P && P.stage <= 2 && P.need > 0 && P.surplus > 0) { save = Math.min(P.need, P.surplus); saveAuto = true; }
  else if (P && P.stage === 4) { SPv = savPeriod(); if (SPv.M > 0) save = SPv.left; }
  const goal = open + save, left = end - goal, days = ahead + 1;
  const r = { stage: P ? P.stage : 0, sav: SPv, end: Math.round(end), open, save, saveAuto, goal: Math.round(goal), left, days, perDay: left / days, endDate: pe, usualRest: null };
  /* עם היסטוריה: כמה בדרך כלל יוצא מהיום ועד סוף התקופה, בהוצאות שאינן חיובים קבועים
     (החיובים הקבועים כבר בתזרים, ולא סופרים אותם פעמיים) */
  if (histPeriods() >= 2) {
    const k = curKey(), ps = periodStart(k), dayIdx = dDays(t0, ps), isFixed = fixedMatcher();
    const sums = [];
    for (let q = 1; q <= 3; q++) {
      const qk = keyAdd(k, -q), qs = periodStart(qk);
      let has = false, s = 0;
      S.tx.forEach((t) => {
        if (t.month !== qk || t.type !== 'expense') return;
        has = true;
        if (isExcluded(t) || isFixed(t)) return;
        if (dDays(iso10(t.date), qs) >= dayIdx) s += absAmt(t);
      });
      if (has) sums.push(s);
    }
    r.usualRest = median(sums);
  }
  return r;
}

/** הוצאות שוטפות עד עכשיו בתקופה (בלי חיובים קבועים ובלי מוחרגות), עם הפירוט */
export function paceParts() {
  const k = curKey(), ps = periodStart(k), t0 = new Date(todayISO() + 'T12:00:00');
  const days = Math.max(1, Math.round((t0 - ps) / 86400000) + 1), isFixed = fixedMatcher();
  let all = 0, ex = 0, fx = 0;
  const exL = [], fxL = [], vL = [];
  S.tx.forEach((t) => {
    if (t.month !== k || t.type !== 'expense') return;
    const a = absAmt(t);
    all += a;
    if (!filterActive([t]).length) { ex += a; exL.push(t); return; }
    if (isFixed(t)) { fx += a; fxL.push(t); return; }
    vL.push(t);
  });
  vL.sort((a, b) => absAmt(b) - absAmt(a));
  return { all, ex, fx, v: all - ex - fx, days, ps, exL, fxL, vL };
}

/** השוואת הוצאות התקופה לאותו יום בשלוש התקופות הקודמות (חציון) */
export function periodPace(k, exp) {
  const ps = periodStart(k), total = dDays(periodEnd(k), ps) + 1;
  const day = Math.max(1, Math.min(total, dDays(todayISO(), ps) + 1));
  const usualNow = [], full = [];
  for (let q = 1; q <= 3; q++) {
    const qk = keyAdd(k, -q), qs = periodStart(qk);
    let tot = 0, up = 0;
    S.tx.forEach((t) => {
      if (t.month !== qk || t.type !== 'expense' || isExcluded(t)) return;
      const a = absAmt(t);
      tot += a;
      if (dDays(iso10(t.date), qs) < day) up += a;
    });
    if (tot > 0) { full.push(tot); usualNow.push(up); }
  }
  const usual = median(usualNow), usualFull = median(full);
  const ok = usualNow.length >= 2 && usual > 0;
  return {
    day, total, usual, usualFull, ok, n: usualNow.length,
    pct: ok ? Math.round((exp / usual - 1) * 100) : 0,
    proj: ok ? Math.round((exp + Math.max(0, usualFull - usual)) / 100) * 100 : null,
    typical: median(full),
  };
}

/** היתרה הצפויה בסוף כל תקופה, נשמרת כל יום. בסוף התקופה זו כבר היתרה בפועל */
export function exitEndUpdate() {
  if (!S.set.balDate) return null;
  const P = exitPlan();
  if (!P) return null;
  const h = Object.assign({}, S.set.exitEnd || {});
  const k = curKey();
  if (h[k] === P.end) return null;
  h[k] = P.end;
  return h;
}

/* ── שגרה שבועית: מה צריך לעשות כדי שהמספרים יישארו נכונים. משימה נעלמת כשהיא בוצעה ── */
export function weeklyTasks({ coverage, revCount, revSample, unclassified }) {
  const tasks = [], t0 = new Date(todayISO() + 'T12:00:00');
  coverage.forEach((c) => {
    const ago = Math.round((t0 - new Date(c.date + 'T12:00:00')) / 86400000);
    if (ago >= 7) tasks.push({ icon: c.src === 'credit' ? '💳' : '🏦', t: 'לסרוק ' + c.label.replace(/^\S+\s/, ''), d: 'נסרק לפני ' + ago + ' ימים', go: 'scan', urgent: ago >= 10 });
  });
  if (histPeriods() < 2) tasks.push({ icon: '🗂️', t: 'לסרוק דוחות של החודשיים הקודמים', d: 'כרטיס ועו"ש. בלעדיהם אין השוואה לקצב הרגיל ואין הערכה לחודשים הבאים', go: 'scan' });
  if (revCount) tasks.push({ icon: '🔤', t: revCount === 1 ? 'שם אחד בעו"ש נראה הפוך' : revCount + ' שמות בעו"ש נראים הפוכים', d: revSample, go: 'revfix' });
  const miss = recOpen('cash').concat(recOpen('credit')).filter((o) => o.st === 'missing');
  if (miss.length) {
    tasks.push({
      icon: '🔁', t: (miss.length === 1 ? 'חיוב קבוע אחד לא נמצא' : miss.length + ' חיובים קבועים לא נמצאו') + ' בסריקה',
      d: miss.slice(0, 2).map((o) => o.r.description).join(', ') + '. לבדוק אם ירדו בשם אחר', go: 'recmiss', urgent: true,
    });
  }
  const P = S.set.balDate ? exitPlan() : null;
  if (P && P.stage === 4) {
    const SP = savPeriod();
    if (SP.left > 0) tasks.push({ icon: '🌱', t: 'להעביר ' + fMoney(SP.left) + ' לחיסכון', d: 'לפי היעדים, לתקופה הזו. אחרי ההעברה הסריקה הבאה תזהה אותה', go: 'goals' });
    const un = savUnassigned().length;
    if (un) tasks.push({ icon: '🔗', t: 'לשייך ' + un + ' העברות לחיסכון', d: 'זוהו בעו"ש ועוד לא שויכו ליעד', go: 'goals' });
  }
  const cur = unclassified.filter((e) => e.sum > 0);
  if (cur.length) {
    const sum = cur.reduce((s, e) => s + e.sum, 0);
    tasks.push({ icon: '🏷️', t: 'לסווג ' + (cur.length === 1 ? 'עסק אחד' : cur.length + ' עסקים'), d: fMoney(sum) + ' ב"אחר"', go: 'classify' });
  }
  return tasks;
}

/** תחזית שנוגעת במסגרת בטווח 45 יום (להתראה בתקופות שאינן הנוכחית) */
export function flowAlert() {
  if (!S.set.balDate) return null;
  const p = project(45);
  if (!p.length) return null;
  const low = lowPoint(p);
  const lim = Number(S.set.odLimit) || 0;
  const over = lim > 0 && low.balance < -lim, deep = lim > 0 && low.balance < -lim * 0.8;
  if (low.balance >= 0 || (!over && !deep)) return null;
  return { low, lim, over };
}

/** מצב חשבון הבנק לפי השפל: ok / warn / bad */
export function limitStatus(lowBal) {
  const lim = Number(S.set.odLimit) || 0;
  if (lowBal >= 0) return { st: 'ok', label: '✓ בטוח' };
  if (lim && lowBal < -lim) return { st: 'bad', label: 'חריגה מהמסגרת' };
  if (lim && lowBal < -lim * 0.8) return { st: 'warn', label: 'קרוב למסגרת' };
  return { st: 'warn', label: 'במינוס' };
}
